import { Bookmark as BookmarkIcon, TrainOutlined } from '@mui/icons-material';
import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';

import { selectImpactCalculateTourForCurrentProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { TourTravel } from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { ScoreText } from '@/ui/components/score-text/ScoreText';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { formatCo2Value } from '@/ui/utils/format-co2-value';

import { ActionMenuForTourTravelItem } from '../ActionMenuForTourTravelItem';

const TOUR_TRAVEL_ITEM_WIDTH = '16rem';

export function TourTravelItem({
  tourTravel,
  onClickDelete,
  onClickOpen,
  isFavorite,
}: {
  tourTravel: TourTravel;
  onClickDelete: (tourTravelId: number) => void;
  onClickOpen: () => void;
  isFavorite: boolean;
}) {
  const calculateTour = useAppSelector(
    selectImpactCalculateTourForCurrentProject,
  );
  const co2TourTravel =
    calculateTour?.co2?.ontour.byTourTravel[tourTravel.id]?.score || 0;

  // eslint-disable-next-line unused-imports/no-unused-vars
  const passengerCount =
    calculateTour?.stats?.ontour?.byTourTravel[tourTravel.id]?.passengerCount;
  // eslint-disable-next-line unused-imports/no-unused-vars
  const weight =
    calculateTour?.stats?.ontour?.byTourTravel[tourTravel.id]?.weight;

  return (
    <Box
      sx={{
        width: TOUR_TRAVEL_ITEM_WIDTH,
        border: `1px solid ${ColorsRaw.anthracite.Anthracite_5}`,
        borderRadius: 2,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Stack sx={{ flexDirection: 'row', gap: 0 }}>
        <ButtonBase
          onClick={onClickOpen}
          sx={{
            paddingY: 0,
            paddingX: 0.5,
            flexGrow: 1,
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <TourTravelDistance tourTravel={tourTravel} />
          </Box>
          <ScoreText
            value={`${formatCo2Value(co2TourTravel)} ${translate('Projects.teCO2')}`}
            variant={'box'}
            boxTextColor={ColorsRaw.secondary.Light_Grey}
            boxBackgroundColor={ColorsRaw.anthracite.Anthracite_4}
          />
        </ButtonBase>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isFavorite && (
            <Tooltip
              title={translate('ProjectsTour.TourTravelFavorite.indicator')}
              placement="top"
            >
              <BookmarkIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </Tooltip>
          )}
          <ActionMenuForTourTravelItem
            onClickDelete={() => onClickDelete(tourTravel.id)}
          />
        </Box>
      </Stack>
    </Box>
  );
}

function TourTravelDistance({ tourTravel }: { tourTravel: TourTravel }) {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 0 }}>
      <TrainOutlined sx={{ color: ColorsRaw.anthracite.Anthracite_5 }} />
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: ColorsRaw.basic.Cream,
        }}
      >
        {tourTravel.travelDistance} km
      </Typography>
    </Stack>
  );
}
